import { Box, Typography, Modal, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useState } from 'react';

const PhotoGallery = ({ photos, title, loading, error }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
  };

  const handleCloseModal = () => {
    setSelectedPhoto(null);
  };

  const handleImageError = (photoId) => {
    setImageErrors(prev => ({ ...prev, [photoId]: true }));
    console.error(`Failed to load image: ${photoId}`);
  };

  return (
    <Box sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Typography
        variant="h3"
        component="h2"
        sx={{
          mb: { xs: 2, sm: 3, md: 4 },
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          background: 'linear-gradient(45deg, #fff, #888)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          px: { xs: 2, sm: 0 },
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(auto-fill, minmax(250px, 1fr))',
            sm: 'repeat(auto-fill, minmax(280px, 1fr))',
            md: 'repeat(auto-fill, minmax(300px, 1fr))',
          },
          gap: { xs: 2, sm: 2.5, md: 3 },
          padding: { xs: 1, sm: 2 },
        }}
      >
        {loading ? (
          <Typography variant="body1" sx={{ gridColumn: '1/-1', textAlign: 'center' }}>
            Loading photos...
          </Typography>
        ) : error ? (
          <Typography variant="body1" sx={{ gridColumn: '1/-1', textAlign: 'center', color: 'error.main' }}>
            Error: {error}
          </Typography>
        ) : photos && photos.length > 0 ? (
          photos.map((photo) => (
            !imageErrors[photo.id] && (
              <motion.div
                key={photo.id}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                onClick={() => handlePhotoClick(photo)}
                style={{
                  cursor: 'pointer',
                  overflow: 'hidden',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                <img
                  src={photo.url}
                  alt={photo.title}
                  onError={() => handleImageError(photo.id)}
                  style={{
                    width: '100%',
                    height: { xs: '250px', sm: '280px', md: '300px' },
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </motion.div>
            )
          ))
        ) : (
          <Typography variant="body1" sx={{ gridColumn: '1/-1', textAlign: 'center' }}>
            No photos found in this category.
          </Typography>
        )}
      </Box>

      <Modal
        open={!!selectedPhoto}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { xs: '95vw', sm: '90vw', md: '80vw' },
            height: { xs: '95vh', sm: '90vh' },
            bgcolor: 'background.paper',
            borderRadius: { xs: 1, sm: 2 },
            p: { xs: 1, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedPhoto && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}
            >
              <img
                src={selectedPhoto.url}
                alt={selectedPhoto.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default PhotoGallery;
